'use client'

import { useCallback, useRef, useState } from 'react'

import { faCircleInfo, faTags } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  AnalyticsSource,
  AnalyticsEntityType,
  ButtonAsA,
  FormattedMessage,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { AllListingsFilterElement } from '@b-stock/search-api-client'

import {
  Content as BaseContent,
  ContentHeader,
  TitleWithIcon,
  Title,
  SearchList,
  SearchCriteriaText,
} from '@/components/homeRecentSearchModule/ui'
import type { DealsRangesConfig } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { buildDealRowsFromConfig } from './dealRowsFromConfig'

const Content = styled(BaseContent)`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  padding: 1rem;
  gap: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: none;
`

const HeaderEnd = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const InfoIconButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: help;
  color: ${Colors.Semantic.BStock.default.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
`

const TooltipContent = styled.div`
  padding: 0.5rem;
`

const StyledSearchList = styled(SearchList)`
  flex: 1;
  min-height: 0;
`

const CtaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  flex-shrink: 0;
`

const PrimaryCta = styled(ButtonAsA)`
  && {
    width: 100%;
    justify-content: center;
  }
`

const SecondaryCta = styled(ButtonAsA)`
  && {
    width: 100%;
    justify-content: center;
  }
`

const DealRow = styled.div<{ $disabled?: boolean }>`
  flex: 1;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};

  &:last-child {
    border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  }
  padding: 0.5rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  transition: all 0.2s ease-in-out;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${(props) =>
      props.$disabled
        ? 'transparent'
        : Colors.Semantic.BStock.backgroundSelected};
  }
`

const DealInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  flex: 1;
  min-width: 0;
`

const DealLabel = styled(SearchCriteriaText)`
  ${Typography.Body3};
  font-weight: 600;
  color: ${Colors.Semantic.Neutral.dark_grey};
  line-height: 1.3;
`

const DealPercentage = styled.span`
  ${Typography.Body4};
  color: ${Colors.Semantic.Neutral.dark_grey};
  line-height: 1.3;
`

const HighlightedValue = styled.span`
  ${Typography.Body3};
  color: ${Colors.Semantic.Info.highlight};
`

const ShopLink = styled(Link)`
  ${Typography.Subtitle2};
  color: ${Colors.Semantic.BStock.onhover};
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: ${Colors.Semantic.BStock.onhover};
    text-decoration: underline;
  }
`

interface BestDealsTrackerProps {
  dealsData?: AllListingsFilterElement[]
  dealsRangesConfig?: DealsRangesConfig
}

export default function BestDealsTracker({
  dealsData,
  dealsRangesConfig,
}: BestDealsTrackerProps) {
  const intl = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const decodedToken = useDecodedAccessToken()
  const accountId = decodedToken?.accountId ?? null

  const [infoIconElement, setInfoIconElement] =
    useState<HTMLButtonElement | null>(null)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setIsTooltipOpen(true)
  }, [])

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsTooltipOpen(false)
      hideTimeoutRef.current = null
    }, 200)
  }, [])

  // Don't render if no deals data at all
  if (!dealsData || dealsData.length === 0) {
    return null
  }

  const dealRows = buildDealRowsFromConfig(dealsData, dealsRangesConfig)

  if (dealRows.length === 0) {
    return null
  }

  const seeMyBidsHref =
    accountId != null ? `/buy/user/${accountId}/bids` : '/all-auctions'

  const handleSeeMoreListingsClick = () => {
    trackButtonClicked({
      screenName: 'homepage',
      buttonName: 'best_deals_tracker_see_more_listings',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      account_id: accountId,
      carousel_name: 'best_deals_tracker',
    })
  }

  const handleSeeMyBidsClick = () => {
    trackButtonClicked({
      buttonName: 'buyer_profile_see_my_bids',
      screenName: 'homepage',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      account_id: accountId,
      carousel_name: 'best_deals_tracker',
    })
  }

  return (
    <Content data-testid="best-deals-tracker">
      <ContentHeader>
        <TitleWithIcon>
          <FontAwesomeIcon icon={faTags} />
          <Title>
            <FormattedMessage id="Search.bestDealsTracker.title" />
          </Title>
        </TitleWithIcon>
        <HeaderEnd>
          <InfoIconButton
            ref={setInfoIconElement}
            aria-label={intl.formatMessage({
              id: 'Search.bestDealsTracker.ariaLabel.info',
            })}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <FontAwesomeIcon icon={faCircleInfo} />
          </InfoIconButton>
          <Tooltip
            referenceElement={infoIconElement}
            placement="bottom"
            hoverable
            isOpen={isTooltipOpen}
          >
            <TooltipContent
              onMouseEnter={showTooltip}
              onMouseLeave={hideTooltip}
            >
              <FormattedMessage
                id="Search.bestDealsTracker.tooltip"
                values={{
                  bold: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </TooltipContent>
          </Tooltip>
        </HeaderEnd>
      </ContentHeader>
      <StyledSearchList>
        {dealRows.map((row) => {
          const hasListings = row.count > 0

          // Create the deals filter URL parameter only if there are listings
          const dealsParam = JSON.stringify([row.dealValue])
          const url = `/all-auctions?deals=${encodeURIComponent(dealsParam)}`

          const bestDealsTrackerOptions = dealRows.reduce<
            Record<string, number>
          >((acc, dealRow) => {
            const key = dealRow.label.toLowerCase().replace(/\s+/g, '_')
            acc[key] = dealRow.count
            return acc
          }, {})

          const handleShopListingsClick = () => {
            trackButtonClicked({
              screenName: 'homepage',
              buttonName: 'shop_listing',
              source: AnalyticsSource.HOME_PORTAL,
              entityType: AnalyticsEntityType.BUYER,
              account_id: accountId,
              results: row.count,
              filter_name: row.label,
              best_deals_tracker_options: [bestDealsTrackerOptions],
              carousel_name: 'best_deals_tracker',
            })
          }

          return (
            <DealRow key={row.label} $disabled={!hasListings}>
              <DealInfo>
                <DealLabel>{row.label}</DealLabel>
                <DealPercentage>
                  <HighlightedValue>
                    <FormattedMessage
                      id="Search.bestDealsTracker.listingsCount"
                      values={{
                        count: row.count,
                      }}
                    />
                  </HighlightedValue>
                </DealPercentage>
              </DealInfo>
              {hasListings && (
                <ShopLink href={url} onClick={handleShopListingsClick}>
                  <FormattedMessage id="Search.bestDealsTracker.shopListings" />
                </ShopLink>
              )}
            </DealRow>
          )
        })}
      </StyledSearchList>
      <CtaStack>
        <PrimaryCta
          href="/all-auctions"
          appearance="primary"
          size="medium"
          onClick={handleSeeMoreListingsClick}
        >
          <FormattedMessage id="Search.bestDealsTracker.seeMoreListings" />
        </PrimaryCta>
        <SecondaryCta
          href={seeMyBidsHref}
          appearance="secondary"
          size="medium"
          onClick={handleSeeMyBidsClick}
        >
          <FormattedMessage id="Search.bestDealsTracker.seeMyBids" />
        </SecondaryCta>
      </CtaStack>
    </Content>
  )
}
